import type { ReactNode } from "react";
import { Icon } from "./Icon";

/* ── PageHeader ─────────────────────────────────────────────────────────
 * Standard top-of-page block: optional breadcrumb, title, supporting copy,
 * and a right-aligned actions slot (where the single gold primary action lives).
 */
export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

export interface PageHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}

export function PageHeader({ eyebrow, title, description, actions, breadcrumbs }: PageHeaderProps) {
  return (
    <header className="fd-page-header">
      {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumb items={breadcrumbs} />}
      <div className="fd-page-header__row">
        <div className="fd-page-header__heading">
          {eyebrow && <div className="fd-eyebrow">{eyebrow}</div>}
          <h1 className="fd-h1">{title}</h1>
          {description && <p className="fd-page-header__desc">{description}</p>}
        </div>
        {actions && <div className="fd-page-header__actions">{actions}</div>}
      </div>
    </header>
  );
}

export function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav className="fd-breadcrumb" aria-label="Breadcrumb">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={i} className="fd-breadcrumb__item">
            {item.onClick && !last ? (
              <button type="button" className="fd-breadcrumb__link" onClick={item.onClick}>
                {item.label}
              </button>
            ) : (
              <span className={last ? "fd-breadcrumb__current" : undefined}>{item.label}</span>
            )}
            {!last && <Icon name="chevronRight" size={14} className="fd-breadcrumb__sep" />}
          </span>
        );
      })}
    </nav>
  );
}

/* ── Toolbar ────────────────────────────────────────────────────────────
 * Filters / search on the left, actions on the right. Wraps on small screens.
 */
export function Toolbar({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <div className="fd-toolbar">
      <div className="fd-toolbar__left">{left}</div>
      <div className="fd-toolbar__right">{right}</div>
    </div>
  );
}

/* ── Section ────────────────────────────────────────────────────────────
 * A titled block of content with optional description and actions. Use to
 * group related cards/tables under a clear heading.
 */
export interface SectionProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

export function Section({ title, description, actions, children }: SectionProps) {
  return (
    <section className="fd-section">
      {(title || actions) && (
        <div className="fd-section__head">
          <div>
            {title && <h2 className="fd-section__title">{title}</h2>}
            {description && <p className="fd-section__desc">{description}</p>}
          </div>
          {actions && <div className="fd-section__actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ── Stack / Grid helpers ───────────────────────────────────────────────
 * Tiny layout helpers so pages don't need ad-hoc inline flex/grid styles.
 */
export function Stack({
  gap = 4,
  children,
  className,
}: {
  gap?: number;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={["fd-stack", className].filter(Boolean).join(" ")}
      style={{ display: "flex", flexDirection: "column", gap: `var(--space-${gap})` }}
    >
      {children}
    </div>
  );
}

export function CardGrid({
  min = 240,
  children,
}: {
  min?: number;
  children: ReactNode;
}) {
  return (
    <div
      className="fd-card-grid"
      style={{
        display: "grid",
        gap: "var(--space-4)",
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      }}
    >
      {children}
    </div>
  );
}
